import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';

/**
 * JWT payload structure for resource authentication tokens.
 */
export interface ResourceJwtPayload {
  sub: string;          // resource_id
  tenant_id: string;
  email: string;
  name: string;
  iat: number;
  exp: number;
  iss: string;          // "newlogicqr-resource-auth"
}

/**
 * Cached credential record stored in IndexedDB for offline login.
 */
interface CachedCredential {
  email: string;
  password_hash: string;
  tenant_id: string;
  resource_id: string;
  name: string;
  cached_at: string;
}

const TOKEN_KEY = 'pwa-token';
const RESOURCE_KEY = 'pwa-resource';
const CREDENTIAL_DB_NAME = 'pwa-credentials';
const CREDENTIAL_STORE_NAME = 'credentials';
const CREDENTIAL_DB_VERSION = 1;

/**
 * AuthService manages JWT token storage, claim extraction, and offline credential caching.
 *
 * Responsibilities:
 * - Store and retrieve JWT token from localStorage
 * - Decode JWT payload to extract resource_id, tenant_id, name, email
 * - Expose reactive signals for authentication state and user identity
 * - Cache credentials (hashed) in IndexedDB for offline login
 * - Redirect to login on logout (triggered by 401 in interceptor)
 *
 * Validates: Requirements 2.4, 9.2
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly router = inject(Router);

  /** Internal signal holding the current JWT token string. */
  private readonly _token = signal<string | null>(this.loadTokenFromStorage());

  /** Signal: true when the user logged in with a temp password and must change it. */
  private readonly _mustChangePassword = signal<boolean>(false);
  readonly mustChangePassword = this._mustChangePassword.asReadonly();

  /** The raw JWT token (readonly signal). */
  readonly token = this._token.asReadonly();

  /** Computed signal: true when a valid non-expired JWT is held. */
  readonly isAuthenticated = computed(() => {
    const token = this._token();
    if (!token) return false;
    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) return false;
    return payload.exp * 1000 > Date.now();
  });

  /** Computed signal: the resource_id from the JWT (sub claim). */
  readonly resourceId = computed(() => {
    const payload = this.getPayload();
    return payload?.sub ?? null;
  });

  /** Computed signal: the tenant_id from the JWT. */
  readonly tenantId = computed(() => {
    const payload = this.getPayload();
    return payload?.tenant_id ?? null;
  });

  /** Computed signal: the resource name from the JWT. */
  readonly resourceName = computed(() => {
    const payload = this.getPayload();
    return payload?.name ?? null;
  });

  /** Computed signal: the resource email from the JWT. */
  readonly resourceEmail = computed(() => {
    const payload = this.getPayload();
    return payload?.email ?? null;
  });

  /**
   * Store token on successful login and optionally persist resource info.
   * When mustChangePassword is true, the auth guard will redirect to force-change-password.
   */
  login(token: string, resource?: { id: string; name: string; email: string }, mustChangePassword = false): void {
    localStorage.setItem(TOKEN_KEY, token);
    if (resource) {
      localStorage.setItem(RESOURCE_KEY, JSON.stringify(resource));
    }
    this._token.set(token);
    this._mustChangePassword.set(mustChangePassword);
  }

  /**
   * Clear the must-change-password state after successful password change.
   */
  clearMustChangePassword(): void {
    this._mustChangePassword.set(false);
  }

  /**
   * Clear token and session, redirect to /login.
   * Called by the HTTP interceptor on 401 responses.
   */
  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(RESOURCE_KEY);
    this._token.set(null);
    this.router.navigate(['/login']);
  }

  /**
   * Get the current token value (for use by HTTP interceptor).
   */
  getToken(): string | null {
    return this._token();
  }

  /**
   * Get decoded claims from the current token.
   */
  getClaims(): ResourceJwtPayload | null {
    return this.getPayload();
  }

  // ---------- Offline Credential Caching (IndexedDB) ----------

  /**
   * Cache credentials for offline login.
   * The password is hashed using SHA-256 before storage (not bcrypt — this is
   * a client-side cache for offline convenience, not a security-critical store).
   */
  async cacheCredentials(
    email: string,
    password: string,
    tenantId: string,
    resourceId: string,
    name: string
  ): Promise<void> {
    const passwordHash = await this.hashPassword(password);
    const record: CachedCredential = {
      email,
      password_hash: passwordHash,
      tenant_id: tenantId,
      resource_id: resourceId,
      name,
      cached_at: new Date().toISOString(),
    };

    const db = await this.openCredentialDb();
    const tx = db.transaction(CREDENTIAL_STORE_NAME, 'readwrite');
    const store = tx.objectStore(CREDENTIAL_STORE_NAME);
    // Use composite key: tenant_id + email
    store.put(record, `${tenantId}:${email}`);
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  }

  /**
   * Perform offline login using cached credentials.
   * On success, creates a synthetic JWT and establishes a session so the app
   * can operate offline using previously stored claims.
   * Returns true on success, false if credentials don't match or aren't cached.
   */
  async offlineLogin(email: string, password: string, tenantId: string): Promise<boolean> {
    const cached = await this.verifyCachedCredentials(email, password, tenantId);
    if (!cached) {
      return false;
    }

    // Create a synthetic offline token so the app can function
    const payload: ResourceJwtPayload = {
      sub: cached.resource_id,
      tenant_id: tenantId,
      email: cached.email,
      name: cached.name,
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 86400, // 24h expiry
      iss: 'newlogicqr-resource-auth-offline',
    };

    const syntheticToken = this.createSyntheticToken(payload);
    this.login(syntheticToken, {
      id: cached.resource_id,
      name: cached.name,
      email: cached.email,
    });
    return true;
  }

  /**
   * Verify cached credentials for offline login.
   * Returns the cached resource info if credentials match, null otherwise.
   */
  async verifyCachedCredentials(
    email: string,
    password: string,
    tenantId: string
  ): Promise<{ email: string; resource_id: string; name: string } | null> {
    try {
      const db = await this.openCredentialDb();
      const tx = db.transaction(CREDENTIAL_STORE_NAME, 'readonly');
      const store = tx.objectStore(CREDENTIAL_STORE_NAME);
      const key = `${tenantId}:${email}`;

      return new Promise((resolve, reject) => {
        const request = store.get(key);
        request.onsuccess = async () => {
          db.close();
          const record = request.result as CachedCredential | undefined;
          if (!record) {
            resolve(null);
            return;
          }

          const inputHash = await this.hashPassword(password);
          if (record.password_hash === inputHash) {
            resolve({
              email: record.email,
              resource_id: record.resource_id,
              name: record.name,
            });
          } else {
            resolve(null);
          }
        };
        request.onerror = () => {
          db.close();
          reject(request.error);
        };
      });
    } catch {
      return null;
    }
  }

  // ---------- Private helpers ----------

  /** Load token from localStorage on service init. */
  private loadTokenFromStorage(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /** Create a synthetic JWT for offline sessions (not cryptographically signed). */
  private createSyntheticToken(payload: ResourceJwtPayload): string {
    const header = { alg: 'none', typ: 'JWT' };
    const encode = (obj: object) =>
      btoa(JSON.stringify(obj)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return `${encode(header)}.${encode(payload)}.offline`;
  }

  /** Get the decoded payload from the current token signal. */
  private getPayload(): ResourceJwtPayload | null {
    const token = this._token();
    if (!token) return null;
    return this.decodeToken(token);
  }

  /** Decode a JWT payload without signature verification (client-side only). */
  private decodeToken(token: string): ResourceJwtPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1]));
      return payload as ResourceJwtPayload;
    } catch {
      return null;
    }
  }

  /** Hash a password using SHA-256 (for offline credential cache). */
  private async hashPassword(password: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /** Open (or create) the IndexedDB database for credential caching. */
  private openCredentialDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(CREDENTIAL_DB_NAME, CREDENTIAL_DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(CREDENTIAL_STORE_NAME)) {
          db.createObjectStore(CREDENTIAL_STORE_NAME);
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}
